/* eslint-disable react/display-name */
import React from 'react';
import axios from 'helpers/axios';
import { format } from 'date-fns';

import { Image, Modal, ModalPosition, ConfirmationModal } from 'ui/components/styled';
import { DataTable, DataTableCell, IconButton } from 'ui/components/shared';
import { getNumberOfPages, recalculatePage } from 'ui/components/shared/DataTable/helpers';
import { EditCompany } from '../forms';
import { ConfirmRemoveCompany } from '../modals';
import { useToasts, useOpenState, useMutableRef, useCancelToken } from 'hooks';

export const CompaniesList = ({ user, DrawerCloseButton }) => {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [totalRows, setTotalRows] = React.useState(0);
  const [perPage, setPerPage] = React.useState(15);
  const [sort, setSort] = React.useState({ column: '', order: '' });
  const [search, setSearch] = React.useState('');
  const [modal, openModal, closeModal] = useOpenState();
  const { errorToast, removeAllToasts } = useToasts();
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const { newCancelToken, cancelPreviousRequest, isCancel } = useCancelToken();
  const columns = [
    {
      name: 'Company name',
      selector: 'name',
      sortable: true,
    },
    {
      name: 'Role',
      selector: 'member.role',
      sortable: true,
      cell: (row) => (
        <DataTableCell>
          <span>{row.member.role}</span>
          {row.member.role === 'admin' && (
            <Image width="9px" mx="xxs" src="/assets/img/icons/star.svg" alt="star" />
          )}
        </DataTableCell>
      ),
      style: {
        textTransform: 'capitalize',
      },
    },
    {
      name: 'Status',
      selector: 'enabled',
      cell: (row) => {
        let status, color;
        switch (row.enabled) {
          case true:
            status = 'Enabled';
            color = '#00AA22';
            break;
          case false:
          default:
            status = 'Disabled';
            if (row.disabled_at) {
              status += ` since ${format(new Date(row.disabled_at), 'MM/dd/yyyy HH:mm')}`;
            }
            color = '#F74C4C';
            break;
        }

        return (
          status && (
            <DataTableCell title={status} color={color} bold>
              {status}
            </DataTableCell>
          )
        );
      },
      sortable: true,
    },
    {
      name: 'Action',
      right: true,
      button: true,
      ignoreRowClick: true,
      cell: (row) => (
        <>
          {!row.enabled && (
            <IconButton
              title="Re-enable"
              variant="transparent"
              icon="check"
              size="24px"
              mx="xxs"
              onClick={() => openModal('confirm-enable-company', { company: row })}
            />
          )}
          <IconButton
            title="Edit"
            variant="transparent"
            icon="edit"
            mx="xxs"
            onClick={() => openModal('edit-company', { company: row })}
          />
          {row.enabled ? (
            <IconButton
              title="Disable"
              variant="transparent"
              icon="disable"
              size="22px"
              mx="xxs"
              onClick={() => openModal('confirm-disable-company', { company: row })}
            />
          ) : (
            <IconButton
              title="Remove"
              variant="transparent"
              icon="trash"
              mx="xxs"
              onClick={() => openModal('confirm-remove-company', { company: row })}
            />
          )}
        </>
      ),
    },
  ];

  const fetchCompanies = async (page) => {
    setLoading(true);

    try {
      const skip = page > 1 ? (page - 1) * perPage : 0;
      const response = await axios.get(
        `/companies/user/${user?.id}/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Companies.' });
    }

    setLoading(false);
  };

  const handleRowsPerPageChange = async (newPerPage, page) => {
    setLoading(true);
    setCurrentPage(page);

    try {
      const skip = page > 1 ? (page - 1) * newPerPage : 0;
      const response = await axios.get(
        `/companies/user/${user?.id}/?$top=${newPerPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setPerPage(newPerPage);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Rows Change.' });
    }

    setLoading(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCompanies(page);
  };

  const handleSort = async (column, sortDirection) => {
    setLoading(true);

    const sortColumn = column?.selector || '';
    const sortOrder = sortColumn ? sortDirection : '';
    setSort({ column: sortColumn, order: sortOrder });

    try {
      const skip = 0;
      const response = await axios.get(
        `/companies/user/${user?.id}/?$top=${perPage}&$skip=${skip}&$orderby=${sortColumn}&$order=${sortOrder}&$search=${search}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Sort Companies.' });
    }

    setLoading(false);
  };

  const handleFilter = async (value) => {
    setLoading(true);
    setSearch(value);

    cancelPreviousRequest();

    try {
      const skip = 0;
      const response = await axios.get(
        `/companies/user/${user?.id}/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${value}`,
        { cancelToken: newCancelToken() }
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
      }
    } catch (error) {
      if (isCancel(error)) return;
      errorToast({ error, default: 'Error: Filter Companies.' });
    }

    setLoading(false);
  };

  const refresh = (newRowsCount) => {
    if (newRowsCount > 0) {
      const updatedPage = getNumberOfPages(newRowsCount, perPage);
      const recalculatedPage = recalculatePage(currentPage.current, updatedPage);
      if (recalculatedPage !== currentPage.current) {
        setCurrentPage(recalculatedPage);
      }
    }

    fetchCompanies(currentPage.current);
  };

  const disableCompany = async (company) => {
    try {
      const response = await axios.post(`/companies/${company.id}/disable`);

      if (response.status === 200) {
        removeAllToasts();
        refresh();
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Disable Company.' });
    }

    closeModal();
  };

  const enableCompany = async (company) => {
    try {
      const response = await axios.post(`/companies/${company.id}/enable`);

      if (response.status === 200) {
        removeAllToasts();
        refresh();
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Enable Company.' });
    }

    closeModal();
  };

  const removeCompany = async (company) => {
    try {
      const response = await axios.delete(`/companies/${company.id}`);

      if (response.status === 204) {
        removeAllToasts();
        refresh(totalRows - 1);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Remove Company.' });
    }

    closeModal();
  };

  React.useEffect(() => {
    fetchCompanies(1);
  }, []);

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        progressPending={loading}
        paginationPerPage={perPage}
        paginationTotalRows={totalRows}
        paginationCurrentPage={currentPage.current}
        onRowsPerPageChange={handleRowsPerPageChange}
        onPageChange={handlePageChange}
        onPageReset={setCurrentPage}
        onSort={handleSort}
        filterPlaceholder="Companies Search"
        onFilter={handleFilter}
        subHeaderTitle={user?.name.full}
        subHeaderCloseButton={DrawerCloseButton}
        noPadding
      />
      <Modal
        show={modal.isOpen('edit-company')}
        onClose={closeModal}
        position={ModalPosition.TOP_CENTER}
        top="10%"
        width="40%"
        maxWidth="650px"
      >
        {modal.isOpen('edit-company') && (
          <EditCompany company={modal.params?.company} onClose={closeModal} onSuccess={refresh} />
        )}
      </Modal>
      <ConfirmationModal
        show={modal.isOpen('confirm-disable-company')}
        onClose={closeModal}
        onConfirm={() => disableCompany(modal.params?.company)}
      >
        Are you sure you want to disable company &quot;
        {modal.params?.company?.name}&quot; ?
      </ConfirmationModal>
      <ConfirmationModal
        show={modal.isOpen('confirm-enable-company')}
        onClose={closeModal}
        onConfirm={() => enableCompany(modal.params?.company)}
      >
        Are you sure you want to re-enable company &quot;
        {modal.params?.company?.name}&quot; ?
      </ConfirmationModal>
      <ConfirmRemoveCompany
        show={modal.isOpen('confirm-remove-company')}
        onClose={closeModal}
        onConfirm={() => removeCompany(modal.params?.company)}
        params={modal.params}
        confirmed={false}
      />
    </>
  );
};
